import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { calcRequiredFTE, calcEffectiveHours, calcLocalization, calcWasteFootprint, calcFutureStateWave } from "./calculations";

// Default SAGE palette
const DEFAULT_PRIMARY = [75, 94, 77];     // #4B5E4D
const DEFAULT_ACCENT = [124, 154, 126];   // #7C9A7E
const SAGE_DARK = [28, 36, 29];
const SAGE_LIGHT = [197, 214, 199];
const SAGE_BG = [250, 250, 247];
const WHITE = [255, 255, 255];
const AMBER = [217, 119, 6];

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace("#", "").match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

async function loadImageDataURL(url) {
  try {
    if (!url) return null;
    const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const res = await fetch(fullUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

// Landscape A4: 297 × 210 mm
const MARGIN = 10;

const TIMWOOD_BUCKETS = [
  { key: "T", name: "Transport", desc: "Unnecessary movement of information or documents" },
  { key: "I", name: "Inventory", desc: "Excess WIP, backlogs, or queued cases" },
  { key: "M", name: "Motion", desc: "Unnecessary system navigation, manual lookups" },
  { key: "W", name: "Waiting", desc: "Time lost awaiting approvals, information" },
  { key: "O1", name: "Overprocessing", desc: "Excess checks, duplicate steps" },
  { key: "O2", name: "Overproduction", desc: "Outputs produced before needed" },
  { key: "D", name: "Defects", desc: "Errors, rework, quality failures" },
  { key: "S", name: "Skills", desc: "Under-utilisation of people capability" },
];

// Runtime theme — overwritten at the start of each export by branding config
let SAGE_MID = [...DEFAULT_PRIMARY];
let SAGE_GREEN = [...DEFAULT_ACCENT];

function addPageHeader(doc, orgName, domain, pageNum, totalPages, firmName) {
  const W = doc.internal.pageSize.width;
  doc.setFillColor(...SAGE_MID);
  doc.rect(0, 0, W, 10, "F");
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  const brand = firmName ? `${firmName}  ·  Lean Toolbox` : "TheKaizenCabinet  ·  Your Lean Toolbox";
  doc.text(brand, MARGIN, 6.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`${orgName || ""}  ·  ${domain || ""}`, W / 2, 6.5, { align: "center" });
  doc.text(`Page ${pageNum} / ${totalPages}`, W - MARGIN, 6.5, { align: "right" });
  doc.setTextColor(...SAGE_DARK);
}

function addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName) {
  const W = doc.internal.pageSize.width;
  const H = doc.internal.pageSize.height;
  doc.setDrawColor(...SAGE_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, H - 8, W - MARGIN, H - 8);
  doc.setFontSize(7);
  doc.setTextColor(...SAGE_GREEN);
  const leftText = hidePoweredBy
    ? `${firmName || ""}  ·  ${generatedAt}`
    : `Generated by TheKaizenCabinet  ·  ${generatedAt}`;
  doc.text(leftText, MARGIN, H - 4);
  doc.text(footerText || "Confidential — For internal use only", W - MARGIN, H - 4, { align: "right" });
  doc.setTextColor(...SAGE_DARK);
}

function sectionTitle(doc, title, y) {
  const W = doc.internal.pageSize.width;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SAGE_MID);
  doc.text(title, MARGIN, y);
  doc.setDrawColor(...SAGE_GREEN);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y + 1.5, W - MARGIN, y + 1.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SAGE_DARK);
  return y + 6;
}

function ensurePage(doc, y, spaceNeeded = 40, headerCtx) {
  const H = doc.internal.pageSize.height;
  if (y + spaceNeeded > H - 12) {
    addPageFooter(doc, headerCtx.generatedAt, headerCtx.footerText, headerCtx.hidePoweredBy, headerCtx.firmName);
    doc.addPage();
    headerCtx.pageCount += 1;
    addPageHeader(doc, headerCtx.orgName, headerCtx.domain, headerCtx.pageCount, "—", headerCtx.firmName);
    return 16;
  }
  return y;
}

// Tally total pages and re-stamp headers after build by using internal pages
function stampPageNumbers(doc) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const W = doc.internal.pageSize.width;
    // overwrite the "x / —" portion in the header
    doc.setFillColor(...SAGE_MID);
    doc.rect(W - 40, 0, 40, 10, "F");
    doc.setTextColor(...WHITE);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} / ${total}`, W - MARGIN, 6.5, { align: "right" });
    doc.setTextColor(...SAGE_DARK);
  }
}

function getBaseTable() {
  return {
    margin: { left: MARGIN, right: MARGIN },
    styles: { fontSize: 8.5, cellPadding: 2, textColor: SAGE_DARK, overflow: "linebreak" },
    headStyles: { fillColor: SAGE_MID, textColor: WHITE, fontStyle: "bold", fontSize: 8.5 },
    alternateRowStyles: { fillColor: [248, 250, 248] },
  };
}

export async function exportDiagnosticPDF({ project, company, diagnostic: diag, branding }) {
  if (!diag) return;

  // ── Apply theme from branding (if provided) ────────────────────────
  const primary = branding?.primary_color ? hexToRgb(branding.primary_color) : null;
  const accent = branding?.accent_color ? hexToRgb(branding.accent_color) : null;
  SAGE_MID = primary || [...DEFAULT_PRIMARY];
  SAGE_GREEN = accent || [...DEFAULT_ACCENT];
  const firmName = branding?.firm_name || "";
  const footerText = branding?.footer_text || "";
  const hidePoweredBy = !!branding?.hide_powered_by;
  const logoDataUrl = branding?.logo_url ? await loadImageDataURL(branding.logo_url) : null;

  const setup = project?.setup || {};
  const org = setup.org_profile || {};
  const vc = setup.volume_capacity || {};
  const qw = setup.quality_waste || {};
  const ehRaw = setup.effective_hours || {};
  const cal = project?.calibration || {};
  const stages = project?.stages || [];
  const eff = calcEffectiveHours(ehRaw);
  const stats = diag.summary_stats || {};
  const wi = diag.workforce_intel || {};
  const currency = org.currency || vc.currency || "USD";
  const totalVolume = vc.total_monthly_volume || diag.total_volume || 0;
  const orgName = diag.org_name || org.company_name || company?.name || "Organisation";
  const domain = diag.domain || org.domain || "—";
  const costPerFTE = vc.cost_per_fte || 15000;
  const generatedAt = diag.generated_at ? new Date(diag.generated_at).toLocaleString() : new Date().toLocaleString();

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const baseTable = getBaseTable();
  const W = doc.internal.pageSize.width;
  const headerCtx = { orgName, domain, generatedAt, pageCount: 1, footerText, hidePoweredBy, firmName };
  let y = 0;

  // ─── PAGE 1: Cover + Executive Summary ──────────────────────────────
  addPageHeader(doc, orgName, domain, 1, "—", firmName);
  y = 18;

  // Title block
  doc.setFillColor(...SAGE_BG);
  doc.rect(MARGIN, y, W - MARGIN * 2, 26, "F");
  doc.setDrawColor(...SAGE_LIGHT);
  doc.rect(MARGIN, y, W - MARGIN * 2, 26);
  if (logoDataUrl) {
    try { doc.addImage(logoDataUrl, "PNG", MARGIN + 4, y + 4, 18, 18, undefined, "FAST"); } catch { /* ignore */ }
  }
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SAGE_MID);
  doc.text("Lean Workforce Diagnostic", W / 2, y + 10, { align: "center" });
  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SAGE_DARK);
  doc.text(orgName, W / 2, y + 17, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(...SAGE_GREEN);
  const subtitle = firmName ? `${firmName}  ·  ${domain}  ·  ${org.department || company?.department || ""}  ·  ${generatedAt}` : `${domain}  ·  ${org.department || company?.department || ""}  ·  Generated: ${generatedAt}`;
  doc.text(subtitle, W / 2, y + 22, { align: "center" });
  y += 32;

  y = sectionTitle(doc, "1.  Executive Summary", y);
  const summaryRows = [
    ["Total Monthly Volume", (totalVolume || 0).toLocaleString(), "Total FTE (Current)", String(stats.total_current_fte || 0)],
    ["Stages Analysed", String(diag.stage_count || stages.length), "Required FTE (Model)", String(stats.total_required_fte || 0)],
    ["Headcount Delta", `${stats.headcount_delta >= 0 ? "+" : ""}${stats.headcount_delta || 0}`, "Monthly Cost", `${currency} ${(stats.monthly_cost || 0).toLocaleString()}`],
    ["Localisation Status", (wi.localization_status || "—").replace("_", " "), "Potential Saving/Mo", `${currency} ${(stats.potential_saving || 0).toLocaleString()}`],
    ["Local Nationals", `${wi.local_count || 0} (${wi.local_pct || 0}%)`, "Expatriates", `${wi.expat_count || 0}`],
  ];
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Metric", "Value", "Metric", "Value"]],
    body: summaryRows,
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      1: { cellWidth: 72 },
      2: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      3: { cellWidth: 72 },
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  y = sectionTitle(doc, "2.  Workforce Composition Intelligence", y);
  const wiRows = [
    ["Monthly Hrs — Expat", `${wi.monthly_hrs_expat || 0} hrs`, "Monthly Hrs — Local", `${wi.monthly_hrs_local || 0} hrs`],
    ["Conversion Factor", `1 local = ${wi.conversion_factor || 0}× expat`, "Localisation Target", `${wi.localization_target || 0}%`],
  ];
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Metric", "Value", "Metric", "Value"]],
    body: wiRows,
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      1: { cellWidth: 72 },
      2: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      3: { cellWidth: 72 },
    },
  });
  y = doc.lastAutoTable.finalY + 3;
  if (wi.emiratisation_alert) {
    doc.setFillColor(254, 243, 199);
    doc.setDrawColor(...AMBER);
    doc.rect(MARGIN, y, W - MARGIN * 2, 9, "FD");
    doc.setFontSize(8);
    doc.setTextColor(...AMBER);
    doc.text("Emiratisation Alert: Local national mix exceeds 30%. Ensure FTE model accounts for capacity differential (local vs expat hrs/month) when planning roster coverage.", MARGIN + 3, y + 5.5, { maxWidth: W - MARGIN * 2 - 6 });
    doc.setTextColor(...SAGE_DARK);
    y += 12;
  }
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 2: Setup & Context ────────────────────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;

  y = sectionTitle(doc, "3.  Setup & Context", y);
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Organisation Profile", "Value", "Volume & Capacity", "Value"]],
    body: [
      ["Organisation", org.company_name || company?.name || "—", "Total Monthly Volume", (vc.total_monthly_volume || 0).toLocaleString()],
      ["Department / BU", org.department || company?.department || "—", "Working Days / Month", String(vc.working_days_per_month || 22)],
      ["Country / Region", org.country || company?.country || "—", "Cost per FTE / Month", `${currency} ${(costPerFTE || 0).toLocaleString()}`],
      ["Business Domain", domain, "Target SLA %", `${vc.target_sla || 95}%`],
      ["Currency", currency, "", ""],
      ["Localisation Target", `${org.localization_target || 20}%`, "", ""],
      ["Tolerance Band", `±${org.tolerance_band || 5}pp`, "", ""],
    ],
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 58 },
      1: { cellWidth: 80 },
      2: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 58 },
      3: { cellWidth: 80 },
    },
  });
  y = doc.lastAutoTable.finalY + 5;

  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Quality & Waste", "Value", "Threshold / Benchmark", "Status"]],
    body: [
      ["Rework / Error Rate", `${qw.rework_rate || 0}%`, "Lean target < 5%", (qw.rework_rate || 0) > 5 ? "Above target" : "On target"],
      ["NVA Time", `${qw.nva_time || 0}%`, "Benchmark < 15%", (qw.nva_time || 0) > 15 ? "Above target" : "On target"],
      ["Absenteeism", `${qw.absenteeism_rate || 0}%`, "Benchmark < 5%", (qw.absenteeism_rate || 0) > 5 ? "Above target" : "On target"],
      ["Annual Attrition", `${qw.attrition_rate || 0}%`, "Benchmark < 15%", (qw.attrition_rate || 0) > 15 ? "Above target" : "On target"],
      ["Onboarding Duration", `${qw.onboarding_weeks || 4} weeks`, "Best practice < 4 wks", (qw.onboarding_weeks || 4) > 4 ? "Above target" : "On target"],
    ],
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 58 },
      1: { cellWidth: 40, halign: "center" },
      2: { cellWidth: 70 },
      3: { cellWidth: 48, halign: "center" },
    },
    didParseCell: (data) => {
      if (data.column.index === 3 && data.section === "body" && data.cell.raw === "Above target") {
        data.cell.styles.fillColor = [254, 226, 226];
        data.cell.styles.textColor = [185, 28, 28];
      }
    },
  });
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 3: Process Stages Detailed ────────────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;
  y = sectionTitle(doc, "4.  Process Stages — Detailed Calculations", y);

  if (stages.length) {
    const stageRows = stages.map((s) => {
      const reqFTE = calcRequiredFTE(s, totalVolume, eff);
      const curr = (s.local_count || 0) + (s.expat_count || 0);
      const loc = calcLocalization(s, org, eff);
      const delta = curr - reqFTE;
      const flag = loc.flag === "ok" ? "On target" : loc.flag === "over" ? "Above target" : "Below target";
      return [
        s.stage_name || "—",
        s.stage_type || "—",
        s.priority || "Medium",
        `${s.yield_pct || 100}%`,
        `${s.aht || 10}m`,
        String(s.local_count || 0),
        String(s.expat_count || 0),
        String(curr),
        `${s.utilization || 75}%`,
        `${s.productivity || 80}%`,
        reqFTE.toFixed(1),
        `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}`,
        `${loc.fteCountBasis}%`,
        `${loc.effortWeighted}%`,
        flag,
      ];
    });
    // Totals row
    const totLocal = stages.reduce((a, s) => a + (s.local_count || 0), 0);
    const totExpat = stages.reduce((a, s) => a + (s.expat_count || 0), 0);
    const totCurr = totLocal + totExpat;
    const totReq = stageRows.reduce((a, r) => a + parseFloat(r[10]), 0);
    stageRows.push([
      "TOTAL", "", "", "", "",
      String(totLocal), String(totExpat), String(totCurr),
      "", "",
      totReq.toFixed(1),
      `${(totCurr - totReq) >= 0 ? "+" : ""}${(totCurr - totReq).toFixed(1)}`,
      totCurr > 0 ? `${(totLocal / totCurr * 100).toFixed(1)}%` : "0%",
      "—", "—",
    ]);

    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [["Stage", "Type", "Prio", "Yield", "AHT", "Local", "Expat", "Total", "Util", "Prod", "Req FTE", "Delta", "Loc% (cnt)", "Loc% (eff)", "Flag"]],
      body: stageRows,
      styles: { ...baseTable.styles, fontSize: 7.5 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 42 },
        1: { cellWidth: 22 },
        2: { cellWidth: 14, halign: "center" },
        3: { cellWidth: 14, halign: "center" },
        4: { cellWidth: 14, halign: "center" },
        5: { cellWidth: 14, halign: "center" },
        6: { cellWidth: 14, halign: "center" },
        7: { cellWidth: 14, halign: "center" },
        8: { cellWidth: 14, halign: "center" },
        9: { cellWidth: 14, halign: "center" },
        10: { cellWidth: 16, halign: "center", fontStyle: "bold" },
        11: { cellWidth: 16, halign: "center" },
        12: { cellWidth: 22, halign: "center" },
        13: { cellWidth: 22, halign: "center" },
        14: { cellWidth: 25, halign: "center" },
      },
      didParseCell: (data) => {
        const last = data.row.index === stageRows.length - 1;
        if (last && data.section === "body") {
          data.cell.styles.fillColor = SAGE_LIGHT;
          data.cell.styles.fontStyle = "bold";
        }
        if (data.column.index === 14 && data.section === "body" && !last) {
          const v = data.cell.raw;
          if (v === "Above target") { data.cell.styles.fillColor = [219, 234, 254]; data.cell.styles.textColor = [30, 64, 175]; }
          else if (v === "Below target") { data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [185, 28, 28]; }
          else if (v === "On target") { data.cell.styles.fillColor = [220, 252, 231]; data.cell.styles.textColor = [21, 128, 61]; }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 3;
    doc.setFontSize(7.5);
    doc.setTextColor(...SAGE_GREEN);
    doc.text(`Req FTE = (Volume × Yield × AHT ÷ Prod) ÷ (Blended Monthly Hrs × 60 × Util). Blended hrs = (Local×${eff.monthlyHrsLocal.toFixed(0)} + Expat×${eff.monthlyHrsExpat.toFixed(0)}) ÷ Total headcount.`, MARGIN, y + 3);
  }
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 4: Effective Hours + Calibration ──────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;

  y = sectionTitle(doc, "5.  Effective Hours Engine", y);
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Input Parameter", "Value", "Computed Output", "Value"]],
    body: [
      ["Gross Working Days / Year", String(ehRaw.gross_working_days || 260), "Net Working Days — Expat", eff.netDaysExpat.toFixed(0)],
      ["Public Holidays", String(ehRaw.public_holidays || 10), "Net Working Days — Local", eff.netDaysLocal.toFixed(0)],
      ["Annual Leave — Expat", `${ehRaw.annual_leave_expat || 21} days`, "Monthly Hours — Expat", eff.monthlyHrsExpat.toFixed(1)],
      ["Annual Leave — Local", `${ehRaw.annual_leave_local || 30} days`, "Monthly Hours — Local", eff.monthlyHrsLocal.toFixed(1)],
      ["Sick Leave", `${ehRaw.sick_leave_days || 5} days`, "Annual Hours — Expat", eff.annualHrsExpat.toFixed(0)],
      ["Other Absence", `${ehRaw.other_absence_days || 3} days`, "Annual Hours — Local", eff.annualHrsLocal.toFixed(0)],
      ["Shift Hours — Expat", `${ehRaw.shift_hours_expat || 8} hrs`, "Conversion Factor", `1 local = ${eff.conversionFactor}× expat`],
      ["Shift Hours — Local", `${ehRaw.shift_hours_local || 5} hrs`, "Capacity Gap", `${eff.capacityGapPct}%`],
      ["Prayer / Break", `${ehRaw.prayer_break || 0.5} hrs`, "", ""],
    ],
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      1: { cellWidth: 72 },
      2: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      3: { cellWidth: 72 },
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  y = sectionTitle(doc, "6.  Calibration — Utilisation & Productivity", y);
  const calRows = [[
    "Global baseline",
    `${cal.global_utilization || 75}%`,
    `${cal.global_productivity || 80}%`,
    "—", "—",
  ]];
  stages.forEach((s, i) => {
    const stageId = s.id || i;
    const ov = cal.stage_overrides?.[stageId] || {};
    calRows.push([
      s.stage_name || "—",
      `${s.utilization || 75}%`,
      `${s.productivity || 80}%`,
      ov.utilization != null ? `${ov.utilization}%` : "—",
      ov.productivity != null ? `${ov.productivity}%` : "—",
    ]);
  });
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Scope", "Util %", "Prod %", "Override Util", "Override Prod"]],
    body: calRows,
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 80 },
      1: { cellWidth: 30, halign: "center" },
      2: { cellWidth: 30, halign: "center" },
      3: { cellWidth: 40, halign: "center" },
      4: { cellWidth: 40, halign: "center" },
    },
  });
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 5: Current State + Waste Footprint ────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;

  y = sectionTitle(doc, "7.  Current State — Operational KPIs", y);
  const totLocal = stages.reduce((a, s) => a + (s.local_count || 0), 0);
  const totExpat = stages.reduce((a, s) => a + (s.expat_count || 0), 0);
  const totCurr = totLocal + totExpat;
  const totReq = stages.reduce((a, s) => a + calcRequiredFTE(s, totalVolume, eff), 0);
  const avgUtil = stages.length ? (stages.reduce((a, s) => a + (s.utilization || 75), 0) / stages.length).toFixed(1) : 0;
  const avgAHT = stages.length ? (stages.reduce((a, s) => a + (s.aht || 10), 0) / stages.length).toFixed(1) : 0;
  const monthlyCost = totCurr * costPerFTE;
  const localPct = totCurr > 0 ? (totLocal / totCurr * 100).toFixed(1) : "0.0";

  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["KPI", "Value", "KPI", "Value"]],
    body: [
      ["Total FTE", String(totCurr), "Monthly Cost", `${currency} ${monthlyCost.toLocaleString()}`],
      ["Required FTE", totReq.toFixed(1), "Headcount Delta", `${(totCurr - totReq) >= 0 ? "+" : ""}${(totCurr - totReq).toFixed(1)}`],
      ["Local Nationals", `${totLocal} (${localPct}%)`, "Expatriates", String(totExpat)],
      ["Average Utilisation", `${avgUtil}%`, "Average AHT", `${avgAHT} min`],
    ],
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      1: { cellWidth: 72 },
      2: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 66 },
      3: { cellWidth: 72 },
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  const waste = calcWasteFootprint(qw, totCurr);
  y = sectionTitle(doc, "8.  Waste Footprint Analysis", y);
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Waste Bucket", "Rate", "FTE-equivalent Impact", "Lean Benchmark"]],
    body: [
      ["NVA Time", `${waste.nva}%`, `${waste.nvaFteHours} FTE-hrs/mo`, "< 15%"],
      ["Rework Load", `${waste.rework}%`, `${waste.reworkFteHours} FTE-hrs/mo`, "< 5%"],
      ["Absenteeism", `${waste.absenteeism}%`, `${waste.absentFte} FTE-equivalent`, "< 5%"],
      ["Total Waste %", `${waste.totalWastePct}%`, "Combined impact", "< 10%"],
    ],
    columnStyles: {
      0: { fillColor: SAGE_BG, fontStyle: "bold", cellWidth: 60 },
      1: { cellWidth: 40, halign: "center" },
      2: { cellWidth: 80 },
      3: { cellWidth: 40, halign: "center" },
    },
  });
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 6: Future State Waves ─────────────────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;

  y = sectionTitle(doc, "9.  Future State Scenarios — Three Improvement Waves", y);
  const waveDefs = [
    { n: 1, title: "Wave 1 — Quick Wins", horizon: "0–3 months", prod: 8, util: 5 },
    { n: 2, title: "Wave 2 — Structural", horizon: "3–9 months", prod: 18, util: 10 },
    { n: 3, title: "Wave 3 — Transformation", horizon: "9–18 months", prod: 30, util: 15 },
  ];
  const waveRows = waveDefs.map((w) => {
    const wFTE = calcFutureStateWave(stages, totalVolume, eff, w.n);
    const fteSaving = Math.max(0, totReq - wFTE);
    const monthlySaving = fteSaving * costPerFTE;
    const annualSaving = monthlySaving * 12;
    return [
      w.title,
      w.horizon,
      `+${w.prod}%`,
      `+${w.util}%`,
      wFTE.toFixed(1),
      `-${fteSaving.toFixed(1)}`,
      `${currency} ${monthlySaving.toLocaleString()}`,
      `${currency} ${annualSaving.toLocaleString()}`,
    ];
  });
  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Wave", "Horizon", "Prod Gain", "Util Gain", "Req FTE", "FTE Released", "Monthly Saving", "Annual Saving"]],
    body: waveRows,
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50 },
      1: { cellWidth: 30, halign: "center" },
      2: { cellWidth: 24, halign: "center" },
      3: { cellWidth: 24, halign: "center" },
      4: { cellWidth: 24, halign: "center" },
      5: { cellWidth: 28, halign: "center" },
      6: { cellWidth: 50, halign: "right" },
      7: { cellWidth: 50, halign: "right" },
    },
  });
  y = doc.lastAutoTable.finalY + 3;
  doc.setFontSize(8);
  doc.setTextColor(...SAGE_MID);
  doc.setFont("helvetica", "bold");
  doc.text("Baseline (Current State):", MARGIN, y + 4);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SAGE_DARK);
  doc.text(`${totReq.toFixed(1)} Required FTE  ·  ${currency} ${(totReq * costPerFTE).toLocaleString()} monthly cost`, MARGIN + 52, y + 4);
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 7: TIMWOOD Waste Framework ────────────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;
  y = sectionTitle(doc, `10.  TIMWOOD Waste Framework — ${domain}`, y);

  autoTable(doc, {
    ...baseTable,
    startY: y,
    head: [["Bucket", "Name", "Description", "Domain-Specific Focus"]],
    body: TIMWOOD_BUCKETS.map((b) => [
      b.key,
      b.name,
      b.desc,
      "Run a waste walk and log observations for this bucket during facilitation",
    ]),
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 16, halign: "center", fillColor: SAGE_BG },
      1: { fontStyle: "bold", cellWidth: 40 },
      2: { cellWidth: 100 },
      3: { cellWidth: 120 },
    },
  });
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 8: Priority Actions + Waste Alerts ────────────────────────
  doc.addPage();
  headerCtx.pageCount += 1;
  addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
  y = 16;

  if (diag.priority_actions?.length) {
    y = sectionTitle(doc, "11.  Priority Focus Areas — Top 3 Stages", y);
    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [["Rank", "Stage", "Priority", "Required FTE", "Current FTE", "Delta", "Yield", "AHT"]],
      body: diag.priority_actions.map((s, i) => [
        `#${i + 1}`,
        s.name,
        s.priority,
        String(s.required_fte),
        String(s.current_fte),
        `${s.delta >= 0 ? "+" : ""}${s.delta}`,
        `${s.yield_pct}%`,
        `${s.aht} min`,
      ]),
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 14, halign: "center" },
        1: { fontStyle: "bold", cellWidth: 70 },
        2: { cellWidth: 24, halign: "center" },
        3: { cellWidth: 28, halign: "center" },
        4: { cellWidth: 28, halign: "center" },
        5: { cellWidth: 20, halign: "center" },
        6: { cellWidth: 20, halign: "center" },
        7: { cellWidth: 24, halign: "center" },
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  if (diag.alerts?.length) {
    y = ensurePage(doc, y, 30, headerCtx);
    y = sectionTitle(doc, "12.  Waste Diagnostic Alerts", y);
    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [["Alert Type", "Value", "Severity", "Analysis & Recommendation"]],
      body: diag.alerts.map((a) => [a.type, `${a.value}%`, a.severity, a.message]),
      styles: { ...baseTable.styles, fontSize: 8 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 36 },
        1: { halign: "center", cellWidth: 18 },
        2: { halign: "center", cellWidth: 20 },
        3: { cellWidth: 203 },
      },
      didParseCell: (data) => {
        if (data.column.index === 2 && data.section === "body") {
          const sev = data.cell.raw;
          if (sev === "High") { data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [185, 28, 28]; }
          else if (sev === "Medium") { data.cell.styles.fillColor = [254, 243, 199]; data.cell.styles.textColor = [146, 64, 14]; }
        }
      },
    });
  }
  addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);

  // ─── PAGE 9: Lean Levers ────────────────────────────────────────────
  if (diag.lean_levers?.length) {
    doc.addPage();
    headerCtx.pageCount += 1;
    addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
    y = 16;
    y = sectionTitle(doc, `13.  Domain Lean Levers — ${domain}`, y);
    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [["Lever", "Priority", "Description & Impact", "Tags"]],
      body: diag.lean_levers.map((l) => [l.title, l.priority, l.body, (l.tags || []).join(", ")]),
      styles: { ...baseTable.styles, fontSize: 8 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 60 },
        1: { halign: "center", cellWidth: 22 },
        2: { cellWidth: 155 },
        3: { cellWidth: 40, fontSize: 7, textColor: SAGE_GREEN },
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === "body") {
          const prio = data.cell.raw;
          if (prio === "Critical") { data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [185, 28, 28]; }
          else if (prio === "High") { data.cell.styles.fillColor = [255, 237, 213]; data.cell.styles.textColor = [154, 52, 18]; }
        }
      },
    });
    addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);
  }

  // ─── PAGE 10: Roadmap ───────────────────────────────────────────────
  if (diag.roadmap?.length) {
    doc.addPage();
    headerCtx.pageCount += 1;
    addPageHeader(doc, orgName, domain, headerCtx.pageCount, "—", firmName);
    y = 16;
    y = sectionTitle(doc, "14.  Lean Programme Roadmap", y);
    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [["Phase", "Title", "Timeline", "Description", "Key Actions"]],
      body: diag.roadmap.map((p) => [
        `Phase ${p.phase}`,
        p.title,
        p.months,
        p.description,
        (p.actions || []).map((a, i) => `${i + 1}. ${a}`).join("\n"),
      ]),
      styles: { ...baseTable.styles, fontSize: 8 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 20, halign: "center" },
        1: { fontStyle: "bold", cellWidth: 50 },
        2: { cellWidth: 24, halign: "center" },
        3: { cellWidth: 85 },
        4: { cellWidth: 98 },
      },
    });
    addPageFooter(doc, generatedAt, footerText, hidePoweredBy, firmName);
  }

  // Re-stamp page numbers with final total
  stampPageNumbers(doc);

  const filename = `SAGE_Diagnostic_${(orgName || "Report").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
